<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">{{ $t('editingCompanyConfig') }}</span>
      <v-spacer/>
      <Icon
      iconColor="white"
      icon="mdi-close"
      :small="false"
      large
      :tooltipText="$t('close')"
      @icon-clicked="$emit('close')"
      />
    </v-card-title>
    <v-card-text>
      <FormWrapper
      :buttonText="$t('done')"
      @submit="done()">
        <v-container fluid class="px-0">
          <v-row dense class="px-3">
            <v-col cols="12" md="6" class="pr-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('company') }}</h3>
              <v-text-field
              v-model="config.shortName"
              :label="$t('shortName')"
              data-testid="company-info-short-name"
              color="primary"
              counter
              maxlength="10"
              :rules="[rules.alphanumeric]"
              />
              <v-select
              v-model="config.selectedTimeZone"
              item-text="name"
              item-value="name"
              return-object
              item-color="primary"
              color="black"
              :items="timezones"
              :label="$t('timezone')"
              />
            </v-col>

            <v-col cols="12" md="6" class="pl-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('financial') }}</h3>
              <v-select
              :items="FinancialIntegrationType.enums"
              v-model="config.financialIntegrationType"
              :label="$t('financialIntegration')"
              :menu-props="{ bottom: true, offsetY: true }"
              return-object
              item-text="name">
                <template #append-outer>
                  <Icon
                  dataTestId="accountingcodes-download-button"
                  :small="false"
                  :disabled="!enableDownloadIcon"
                  icon="mdi-file-delimited-outline"
                  iconColor="primary"
                  :tooltipText="$t('accountingCodesCSV')"
                  @icon-clicked="csvDialog = true"
                  />
                </template>
              </v-select>
              <v-select
              v-model="config.adjustingEntriesFrequency"
              :label="$t('adjustingEntriesFrequency')"
              :items="AdjustingEntriesFrequency.enums"
              return-object>
                <template #selection="{item}">
                  {{ $t(item.label) }}
                </template>
                <template #item="{item}">
                  {{ $t(item.label) }}
                </template>
              </v-select>
              <v-select
              v-model="config.biReplicationStorageMode"
              :items="StorageReplicationMode.enums"
              item-text="name"
              return-object
              :label="$t('storageReplicationMode')"
              />
            </v-col>
          </v-row>

          <v-divider class="my-5"/>

          <v-row dense class="px-3">
            <v-col cols="12" md="6" class="pr-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">{{ $t('recurringAccountPayments') }}</h3>
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">
                  <v-select
                  v-model="config.accountPayableTemplateDayOfWeek"
                  item-color="secondary"
                  :label="$t('dayOfWeek')"
                  item-text="name"
                  return-object
                  color="black"
                  :items="SystemDayOfWeek.enums"
                  />
                </v-col>
                <v-col cols="6" class="pl-2">
                  <v-select
                  return-object
                  v-model="config.accountPayableTemplateLocalHour"
                  :label="$t('hour')"
                  :items="[...Array(24).keys()]">
                    <template #item="{item}">
                      {{ moment(item, 'H').format('LT') }}
                    </template>
                    <template #selection="{item}">
                      {{ moment(item, 'H').format('LT') }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="6" class="pl-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
                {{ $t('dailyTicketReport') }}
                <Icon
                v-if="config?.lastDailyTicketReportCreatedAt"
                margin="ml-2"
                icon="mdi-information-outline"
                iconColor="info"
                :tooltipText="$t('lastReportX', { x: utcToLocalDate(config.lastDailyTicketReportCreatedAt) })"
                />
              </h3>
              <v-row no-gutters>
                <v-col cols="12">
                  <v-select
                  v-model="config.localDailyTicketReportHour"
                  :label="$t('hour')"
                  :items="[...Array(24).keys()]">
                    <template #item="{item}">
                      {{ moment(item, 'H').format('LT') }}
                    </template>
                    <template #selection="{item}">
                      {{ moment(item, 'H').format('LT') }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-divider class="my-5"/>

          <v-row dense class="px-3">
            <v-col cols="12" md="6" class="pr-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('settlements') }}</h3>
              <v-checkbox
              class="mt-0 mb-3"
              hide-details
              :label="$t('customSettlementMessages')"
              v-model="customSettlementMessagesEnabled"
              />
              <v-textarea
              outlined
              v-model="config.defaultSettlementMessage"
              :label="$t('defaultSettlementMessage')"
              :disabled="!customSettlementMessagesEnabled"
              counter
              maxlength="512"
              rows="4"
              auto-grow
              :rules="customSettlementMessagesEnabled ? [rules.required] : []"
              />
              <v-select
              v-model="config.settlementReportMode"
              :items="SettlementReportMode.enums"
              item-text="name"
              return-object
              :label="$t('settlementReportMode')"
              />
            </v-col>

            <v-col cols="12" md="6" class="pl-md-5">
              <h3 class="text-subtitle-1 font-weight-bold mb-3">{{ $t('preferences') }}</h3>
              <v-checkbox
              class="mt-0 mb-2"
              hide-details
              :label="$t('requireApprovalForContractModifications')"
              v-model="config.requireApprovalForContractModifications"
              />
              <v-checkbox
              class="mt-0"
              hide-details
              :label="$t('integrateWithSamsara')"
              v-model="config.samsaraIntegration"
              />
              <v-checkbox
              class="mt-2"
              hide-details
              :label="$t('useCustomMapLayers')"
              v-model="config.useCustomMapLayers"
              />
              <v-select
              class="mt-5 mode-select"
              v-model="config.endangeredSpeciesMode"
              :items="endangeredSpeciesModeOptions"
              item-text="name"
              return-object
              hide-details
              :label="$t('endangeredSpeciesMode')">
                <template #selection="{ item }">
                  <div class="d-flex align-center flex-grow-1">
                    {{ item.name }}
                    <Icon
                    v-if="item.value === EndangeredSpeciesMode.SpeciesLayer.value"
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="$t('speciesLayerRequiresProvisionedData')"
                    :small="false"
                    margin="ml-auto"
                    />
                  </div>
                </template>
                <template #item="{ item }">
                  <div class="d-flex align-center flex-grow-1">
                    {{ item.name }}
                    <Icon
                    v-if="item.value === EndangeredSpeciesMode.SpeciesLayer.value"
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="$t('speciesLayerRequiresProvisionedData')"
                    :small="false"
                    margin="ml-auto"
                    />
                  </div>
                </template>
              </v-select>
              <v-select
              v-if="speciesModeEnabled"
              class="mt-5 mode-select"
              v-model="config.dueDiligenceApprovalMode"
              :items="dueDiligenceApprovalModeOptions"
              item-text="name"
              return-object
              hide-details
              :label="$t('dueDiligenceApprovalMode')">
                <template #selection="{ item }">
                  <div class="d-flex align-center flex-grow-1">
                    {{ item.name }}
                    <Icon
                    v-if="item.explanation"
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="item.definition"
                    :small="false"
                    margin="ml-auto"
                    />
                  </div>
                </template>
                <template #item="{ item }">
                  <div class="d-flex align-center flex-grow-1">
                    {{ item.name }}
                    <Icon
                    v-if="item.explanation"
                    icon="mdi-information-outline"
                    iconColor="info"
                    :tooltipText="item.definition"
                    :small="false"
                    margin="ml-auto"
                    />
                  </div>
                </template>
              </v-select>
              <h3 class="text-subtitle-1 font-weight-bold mb-3 mt-5">{{ $t('mobileAppSettings') }}</h3>
              <v-checkbox
              class="mt-0"
              hide-details
              :label="$t('filterContractsToLoaderAccount')"
              v-model="config.filterContractsToLoaderAccount"
              />
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
    <v-dialog v-model="csvDialog" width="400">
      <RadioOptionDialog
      v-if="csvDialog"
      :options="CSVDownloadOptions"
      :title="$t('downloadCSV')"
      :submitButtonText="$t('download')"
      @close-dialog="csvDialog = false"
      @optionChosen="downloadManager"/>
    </v-dialog>
  </v-card>
</template>

<script>
import { isMobile } from '@/utils/DeviceUtility.js'
import { FinancialIntegrationType, AdjustingEntriesFrequency, SystemDayOfWeek, StorageReplicationMode, SettlementEmailMessageMode, EndangeredSpeciesMode, DueDiligenceApprovalMode, SettlementReportMode } from '@/utils/Enumerations.js'
import moment from 'moment'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { mapActions } from 'vuex'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { generateCsvString } from '@/utils/CSVUtility'
import { fileNameForString } from '@/utils/pdf-formatters/GenericSettlementsFunctions.js'
import timezones from './timezones.json'
import { cloneDeep } from 'lodash'
import fieldRules from '@/utils/rules'
import { sanitizeSettlementMessage, containsUrl } from '@/utils/SettlementMessageSanitizer.js'

const PRODUCT_CODES = 'productCodes'
const BYPRODUCT_CODES = 'byproductCodes'
const EXPORT_CODES = 'exportCodes'
const GL_CODES = 'glCodes'
const ALL = 'all'

export default {
  name: 'CompanyConfigForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    RadioOptionDialog: () => import('@/components/helper/RadioOptionDialog.vue')
  },

  props: {
    propOptions: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    FinancialIntegrationType,
    AdjustingEntriesFrequency,
    SystemDayOfWeek,
    StorageReplicationMode,
    EndangeredSpeciesMode,
    SettlementReportMode,
    timezones,
    config: {},
    csvDialog: false,
    rules: fieldRules.rules
  }),

  created () {
    this.config = cloneDeep(this.propOptions)
  },

  watch: {
    'config.endangeredSpeciesMode' (mode) {
      if (mode?.value === EndangeredSpeciesMode.None.value) this.config.dueDiligenceApprovalMode = DueDiligenceApprovalMode.None
    }
  },

  computed: {
    enableDownloadIcon () {
      return this.config.financialIntegrationType.value !== FinancialIntegrationType.None.value
    },

    CSVDownloadOptions () {
      return [PRODUCT_CODES, BYPRODUCT_CODES, EXPORT_CODES, GL_CODES, ALL]
    },

    endangeredSpeciesModeOptions () {
      return [EndangeredSpeciesMode.None, EndangeredSpeciesMode.Manual, EndangeredSpeciesMode.SpeciesLayer]
    },

    dueDiligenceApprovalModeOptions () {
      return [DueDiligenceApprovalMode.None, DueDiligenceApprovalMode.Review, DueDiligenceApprovalMode.Strict]
    },

    speciesModeEnabled () {
      return this.config.endangeredSpeciesMode?.value !== EndangeredSpeciesMode.None.value
    },

    customSettlementMessagesEnabled: {
      get () {
        return this.config.settlementEmailMessageMode?.value === SettlementEmailMessageMode.Default.value
      },
      set (val) {
        this.config.settlementEmailMessageMode = val ? SettlementEmailMessageMode.Default : SettlementEmailMessageMode.None
        if (!val) this.config.defaultSettlementMessage = ''
      }
    }
  },

  methods: {
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('activity-templates', ['fetchActivityTemplates']),
    isMobile,
    moment,
    utcToLocalDate,
    done () {
      if (this.customSettlementMessagesEnabled && containsUrl(this.config.defaultSettlementMessage)) {
        this.setSnackError(this.$t('settlementMessageNoUrls'))
        return
      }
      this.config.defaultSettlementMessage = sanitizeSettlementMessage(this.config.defaultSettlementMessage)
      this.$emit('done-editing', this.config)
    },

    async downloadManager (type) {
      let downloadRequest
      switch (type) {
        case PRODUCT_CODES:
          const products = await this.fetchProducts({
            includeProduction: true,
            includeByProducts: false
          })
          const productCodesCSV = this.downloadProductCodesCSV(products)
          downloadRequest = {
            blob: productCodesCSV.blob,
            filename: productCodesCSV.filename
          }
          break
        case BYPRODUCT_CODES:
          const byproducts = await this.fetchProducts({
            includeProduction: false,
            includeByProducts: true
          })
          const byproductCodesCSV = this.downloadByProductCodesCSV(byproducts)
          downloadRequest = {
            blob: byproductCodesCSV.blob,
            filename: byproductCodesCSV.filename
          }
          break
        case EXPORT_CODES:
          const accounts = await this.fetchAccounts()
          const exportCodesCSV = this.downloadExportCodesCSV(accounts)
          downloadRequest = {
            blob: exportCodesCSV.blob,
            filename: exportCodesCSV.filename
          }
          break
        case GL_CODES:
          const activityTemplates = await this.fetchActivityTemplates()
          const glCodesCSV = this.downloadGLCodesCSV(activityTemplates)
          downloadRequest = {
            blob: glCodesCSV.blob,
            filename: glCodesCSV.filename
          }
          break
        case ALL:
          this.downloadAllFiles()
          break
      }

      if (!downloadRequest) return

      saveAs(downloadRequest.blob, downloadRequest.filename)
    },

    async downloadAllFiles () {
      const requestObjProducts = {
        includeProduction: true,
        includeByProducts: true
      }

      const zip = new JSZip()

      const products = await this.fetchProducts(requestObjProducts)

      const productCodes = this.downloadProductCodesCSV(products.filter(r => !r.isByProduct))
      const byproductCodes = this.downloadByProductCodesCSV(products.filter(r => r.isByProduct))
      zip.file(productCodes.filename, productCodes.blob)
      zip.file(byproductCodes.filename, byproductCodes.blob)

      const accounts = await this.fetchAccounts()
      const exportCodes = this.downloadExportCodesCSV(accounts)
      zip.file(exportCodes.filename, exportCodes.blob)

      const activityTemplates = await this.fetchActivityTemplates()
      const glCodes = this.downloadGLCodesCSV(activityTemplates)
      zip.file(glCodes.filename, glCodes.blob)

      const downloadableZip = await zip.generateAsync({ type: 'blob' })
      saveAs(downloadableZip, `Accounting-Codes-${moment().format()}`)
    },

    downloadProductCodesCSV (products) {
      const csvString = generateCsvString(products, [
        { label: 'name', value: a => a.name },
        { label: 'code', value: a => a.code ? a.code : '' }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const filename = fileNameForString(`Accounting-Product-Codes-${moment().format()}`, 'csv')
      return { blob, filename }
    },

    downloadByProductCodesCSV (products) {
      const csvString = generateCsvString(products, [
        { label: 'name', value: a => a.name },
        { label: 'code', value: a => a.code ? a.code : '' }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const filename = fileNameForString(`Accounting-Byproduct-Codes-${moment().format()}`, 'csv')
      return { blob, filename }
    },

    downloadExportCodesCSV (excodes) {
      const csvString = generateCsvString(excodes, [
        { label: 'name', value: a => a.name },
        { label: 'code', value: a => a.exportCode ? a.exportCode : '' }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const filename = fileNameForString(`Accounting-Export-Codes-${moment().format()}`, 'csv')
      return { blob, filename }
    },

    downloadGLCodesCSV (glCodes) {
      const csvString = generateCsvString(glCodes, [
        { label: 'name', value: a => a.name },
        { label: 'glCode', value: a => a.glCode },
        { label: 'glOffset', value: a => a.glOffset ? a.glOffset : '' }
      ], { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const filename = fileNameForString(`Accounting-GL-Codes-${moment().format()}`, 'csv')
      return { blob, filename }
    }
  }
}
</script>

<style scoped>
.mode-select ::v-deep .v-select__selections input {
  width: 0;
  min-width: 0;
  flex: 0 0 0;
}
</style>
